// "Now & next", at the top of the agents list (AgentsPane: the clinic page's sidebar, docked and popped
// out, and the /agents page): which agents are working now, queued, waiting for a decision, being
// checked, and which run next (nowNextRows, from the list's own answer).
//
// - A click on a row opens that agent: `onOpen`, the handler the list's rows call.
// - Hidden when every group is empty.
// - At most ROWS_SHOWN rows, in the groups' order; "Show all" shows the rest, "Show fewer" folds them.
// - Times are worded in the viewer's time zone from the clock at each render, and while "Next up" (the
//   only times shown) is there the section renders again each minute, so "today" and "tomorrow" stay
//   right past midnight on a page left open. `now` and `timeZone` pin both (tests).
// - A row cut off by a narrow pane has its full words as a tooltip.
// - Light and dark as AgentsPane: Tailwind inside #clinic-root.
//
// Props: agents (useAgents' list); onOpen(agentId); now (optional: a Date or milliseconds);
// timeZone (optional: an IANA zone; the browser's own when none).
import React, { useEffect, useId, useState } from 'react';
import { nowNextRows } from './nowNextRows';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const DASH = String.fromCharCode(0x2014);

export const ROWS_SHOWN = 6;
const MINUTE_MS = 60000;

// AgentsPane's heading and rows.
const HEADING = 'text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5';
const ROW = 'w-full text-left rounded-lg border px-3 py-2';
const ROW_IDLE = 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-800/40';
const GROUP = 'text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1';
const TITLE = 'font-semibold text-slate-800 dark:text-slate-100';
const DETAIL = 'text-slate-500 dark:text-slate-400';
// A decision that can be made now is amber, as the list's "Needs you" is; a halted agent's is grey.
const NEEDS = 'text-amber-700 dark:text-amber-300';
const MORE = 'mt-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:underline';

// AgentChat's spinner: the row's group already says it is working, so screen readers skip it.
function Spinner() {
  return (
    <svg className="w-3 h-3 shrink-0 animate-spin text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity=".25" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// The first `room` rows, group by group; a group with none left is dropped.
function firstRows(groups, room) {
  const shown = [];
  let left = room;
  groups.forEach((group) => {
    if (left <= 0) return;
    const rows = group.rows.slice(0, left);
    left -= rows.length;
    shown.push({ ...group, rows });
  });
  return shown;
}

export default function NowNext({ agents, onOpen, now = null, timeZone = undefined }) {
  const ids = useId();
  const [all, setAll] = useState(false);
  const [, setTick] = useState(0);
  const pinned = now !== null && now !== undefined;
  const groups = nowNextRows(agents, pinned ? now : Date.now(), timeZone);
  // "Next up" holds the only times shown, so the clock runs only while it is there.
  const ticking = !pinned && groups.some((group) => group.key === 'next');
  useEffect(() => {
    if (!ticking) return undefined;
    const clock = setInterval(() => setTick((tick) => tick + 1), MINUTE_MS);
    return () => clearInterval(clock);
  }, [ticking]);

  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  if (!total) return null;
  const shown = all ? groups : firstRows(groups, ROWS_SHOWN);
  const titleId = `${ids}now-next`;
  return (
    <div role="group" aria-labelledby={titleId} className="px-3 py-2">
      <div id={titleId} className={HEADING}>Now &amp; next</div>
      <div className="space-y-2">
        {shown.map((group) => (
          <div key={group.key}>
            <div id={`${ids}${group.key}`} className={GROUP}>{group.title}</div>
            <ul aria-labelledby={`${ids}${group.key}`} className="list-none m-0 p-0 space-y-1.5">
              {group.rows.map((row) => (
                <li key={row.agentId}>
                  <button type="button" onClick={() => onOpen?.(row.agentId)} className={`${ROW} ${ROW_IDLE} flex items-center gap-1.5`}>
                    {group.key === 'working' && <Spinner />}
                    {/* A narrow pane cuts the words off: the tooltip has them all. On the words, not the
                        button, so a screen reader doesn't hear them twice. */}
                    <span className="min-w-0 truncate text-xs" title={`${row.title} ${DASH} ${row.detail}`}>
                      <span className={TITLE}>{row.title}</span>
                      <span className={group.key === 'waiting' && !row.halted ? NEEDS : DETAIL}>{` ${DASH} ${row.detail}`}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {total > ROWS_SHOWN && (
        <button type="button" aria-expanded={all} onClick={() => setAll((open) => !open)} className={MORE}>
          {all ? 'Show fewer' : 'Show all'}
        </button>
      )}
    </div>
  );
}
